// Session persistence seam for the chat-history sidebar.
//
// The UI and the `useSessions` hook depend only on the `SessionStore`
// interface, never on a concrete backend. Today the only implementation is
// `localSessionStore` (browser localStorage), which makes conversation history
// work in local-agent dev without any server. When the multi-tenant Runtime
// lands, a second implementation backed by AgentCore Memory + the trace store
// (behind API Gateway) satisfies the same interface and is swapped in at the
// single call site in `useSessions` — the sidebar and hook do not change.
//
// The interface is async on purpose: a real backend is network-bound, so
// modeling it as Promises now means the swap is a drop-in with no signature
// churn. The local implementation simply resolves immediately.

import type { ChatSession, SessionSummary } from "@/components/chat/types"

/**
 * Storage backend for a single user's conversation history.
 *
 * All methods are scoped to the owner the store was created for; callers never
 * pass an owner id. Ordering of `list` is most-recently-updated first.
 */
export interface SessionStore {
  /**
   * List the owner's saved conversations as lightweight summaries, ordered
   * most-recently-updated first.
   *
   * @returns the session summaries (never null; empty array when none).
   */
  list(): Promise<SessionSummary[]>

  /**
   * Load one full conversation (including its transcript) by id.
   *
   * @param id - the session id to load.
   * @returns the stored session, or null if no session with that id exists.
   */
  get(id: string): Promise<ChatSession | null>

  /**
   * Create or overwrite a conversation. Implementations key on `session.id`.
   *
   * @param session - the full session to persist.
   */
  save(session: ChatSession): Promise<void>

  /**
   * Delete a conversation by id. A missing id is a no-op (idempotent).
   *
   * @param id - the session id to delete.
   */
  remove(id: string): Promise<void>

  /**
   * Rename a conversation. A user-chosen title; does not change `updatedAt`, so
   * renaming never reorders the list. A missing id is a no-op.
   *
   * @param id - the session id to rename.
   * @param title - the new, already-trimmed title.
   */
  rename(id: string, title: string): Promise<void>

  /**
   * Mark a conversation as freshly used: advance its `updatedAt` (and refresh
   * the durable TTL) so the sidebar surfaces recently-active conversations
   * first, WITHOUT changing the title. Called on turn completion. A best-effort
   * recency bump — implementations must never throw in a way that breaks the
   * turn, and creating the record if it does not yet exist is allowed.
   *
   * @param id - the session id to touch.
   */
  touch(id: string): Promise<void>
}

// Bumped only on a breaking change to the persisted shape; isolates old blobs
// from a new schema so a stale entry can never be parsed against a newer type.
const SCHEMA_VERSION = "v1"

/**
 * Build the localStorage key for a given owner. Namespacing by owner keeps two
 * users on a shared browser/profile from seeing each other's history and
 * mirrors the per-`sub` scoping the future server-backed store enforces.
 *
 * @param ownerKey - stable per-user key (Cognito sub, email, or "local").
 * @returns the namespaced storage key.
 */
function storageKey(ownerKey: string): string {
  return `app-sessions:${SCHEMA_VERSION}:${ownerKey}`
}

/**
 * Project a full session down to its sidebar summary.
 *
 * @param session - the full stored session.
 * @returns the summary fields the list view needs.
 */
function toSummary(session: ChatSession): SessionSummary {
  return {
    id: session.id,
    title: session.title,
    createdAt: session.createdAt,
    updatedAt: session.updatedAt,
  }
}

/**
 * Create a localStorage-backed `SessionStore` for one owner.
 *
 * The entire owner history is stored as a single JSON blob (a map of id →
 * session) under one key. Conversation histories are small text transcripts, so
 * a per-owner blob is simpler and fast enough; a server-backed store would
 * instead page a list endpoint.
 *
 * @param ownerKey - stable per-user key used to namespace storage.
 * @returns a `SessionStore` bound to that owner.
 */
export function createLocalSessionStore(ownerKey: string): SessionStore {
  const key = storageKey(ownerKey)

  /**
   * Read and parse the owner's session map from localStorage.
   *
   * A corrupt or unreadable blob is treated as "no history" rather than
   * propagated: this is a presentation-layer cache at the browser boundary, and
   * bricking the whole chat UI because one stored entry is malformed would be a
   * worse failure than starting from an empty list. The corruption is logged so
   * it is not silent.
   *
   * @returns the id → session map (empty object on absence or parse failure).
   */
  function readAll(): Record<string, ChatSession> {
    const raw = localStorage.getItem(key)
    if (!raw) return {}
    try {
      const parsed = JSON.parse(raw) as Record<string, ChatSession>
      return parsed && typeof parsed === "object" ? parsed : {}
    } catch (err) {
      console.warn("Discarding unreadable session history blob:", err)
      return {}
    }
  }

  /**
   * Serialize and write the owner's session map back to localStorage.
   *
   * @param all - the id → session map to persist.
   */
  function writeAll(all: Record<string, ChatSession>): void {
    localStorage.setItem(key, JSON.stringify(all))
  }

  return {
    async list(): Promise<SessionSummary[]> {
      return Object.values(readAll())
        .map(toSummary)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    },

    async get(id: string): Promise<ChatSession | null> {
      return readAll()[id] ?? null
    },

    async save(session: ChatSession): Promise<void> {
      const all = readAll()
      all[session.id] = session
      writeAll(all)
    },

    async remove(id: string): Promise<void> {
      const all = readAll()
      if (id in all) {
        delete all[id]
        writeAll(all)
      }
    },

    async rename(id: string, title: string): Promise<void> {
      const all = readAll()
      const session = all[id]
      if (session) {
        // Keep `updatedAt` untouched: a rename is metadata, not new activity, so
        // it must not jump the conversation to the top of the list.
        all[id] = { ...session, title }
        writeAll(all)
      }
    },

    async touch(): Promise<void> {
      // No-op locally: `persist` already writes a fresh `updatedAt` on every
      // turn, so the local list is already ordered by recency. The seam exists
      // for the remote store, where `save` is a no-op and recency must be
      // bumped on the server instead.
    },
  }
}
